import { Iiso2Codes } from '../models/enviroment-delivery-details.model';
import { AppUtils } from './app.utils';
import {
  IAddressReduced,
  IAddressSelectBox,
  ICompanyBusinessUnitAddresses,
  ICompanyBusinessUnits, ICustomerCheckoutData,
} from '../models/customer.models';
import { IAddress, ICountry } from '../models/common.models';
import { EThankYouPageFieldNames } from '../configurations/common';
import { IShsEquipmentAttributesEquipmentAddress } from '../models/installedbase.models';

export class AddressUtils {

  /**
   * Format address object to string.
   *
   * @param {IAddress | IAddressReduced | null} address
   * @returns {string}
   */
  static formatAddressToString(address: IAddress | IAddressReduced | null): string {
    if (!address) {
      return '';
    }
    const addressDetails: string = [
      address.address1,
      address.city,
      address.state,
      address.zipCode,
    ].filter(item => item && item !== 'empty').join(', ');
    let addressName: string = this.checkIfValueHasValueEmpty(address?.company);
    if (!addressName) {
      addressName = address?.name ?? '';
    }
    if (!this.checkIfValueHasValueEmpty(addressName)) {
      addressName = this.checkIfValueHasValueEmpty(address?.firstName) ?? '';
    }
    const addressLine: string = addressDetails.length > 0 ? `(${addressDetails})` : '';

    if (addressName && addressLine) {
      return `${addressName} ${addressLine}`;
    } else {
      return addressName || addressLine;
    }
  }

  /**
   * Check if the given value is empty.
   * @param {string | undefined} value
   * @returns {string}
   */
  static checkIfValueHasValueEmpty(value: string | undefined): string {
    return value === 'empty' ? '' : value ?? '';
  }

  static createAddressString(address: any): string {
    if (!address) return '';

    const street: string = address.address1 || '';
    const zipCode: string = address.zipCode || '';
    const city: string = address.city || '';
    const state: string = address.address2 || '';
    const country: string = address.country?.name || address.country || '';

    const newAddressFormat: string = [street, `${zipCode},`, city, state, country]
      .filter(item => (item && item !== 'empty' && item !== 'empty,'))
      .join(', ')
      .replace(',,', '');

    if (newAddressFormat.endsWith(',') || newAddressFormat.endsWith(' ')) {
      return newAddressFormat.substring(0, newAddressFormat.length - 1);
    } else {
      return newAddressFormat;
    }
  }

  static createAddressStringForCpq(address: any): string {
    if (!address) return '';

    const company: string = address.name || '';
    const street: string = address.address1 || '';
    const city: string = address.city || '';
    const state: string = address.address2 || '';
    const country: string = address.state || '';
    const zipCode: string = address.zipCode || '';

    const newAddressFormat: string = [company, street, city, state, country, zipCode]
      .filter(item => (item && item !== 'empty' && item !== 'empty,')).join(', ');

    if (newAddressFormat.endsWith(',') || newAddressFormat.endsWith(' ')) {
      return newAddressFormat.substring(0, newAddressFormat.length - 1);
    } else {
      return newAddressFormat;
    }
  }

  static createAddressStringForCpqSelectedBillOrPayer(address: any): string {
    return address.name ? `${address.name}` : '';
  }

  static getBodyRequiredAddress(address: any): any {
    return {
      sapId: address.sapId,
      name: address.company ?? address.name,
      address1: address.address1,
      address2: address.address2,
      address3: address.address3,
      city: address.city,
      phone: address.phone,
      state: address.state,
      zipCode: address.zipCode,
    };
  }

  static changeAddressName(address: IAddress): IAddress {
    return {
      ...address,
      company: address.name,
      name: `${address.name} (${address.address1}, ${address.city}, ${address.state}, ${address.zipCode})`,
    } as IAddress;
  }

  static getCountriesForDeliveryAddress(): Iiso2Codes[] {
    return AppUtils.getCurrentStore().deliveryAddressCountries;
  }

  static getDeliveryCountryByIsoCode(isoCode: string): Iiso2Codes {
    return AppUtils.getCurrentStore().deliveryAddressCountries.find(country => country.value === isoCode)!;
  }

  static appendAddresses(target: Array<any>, newAddresses: Array<any>, type: string, addressFromSystem: boolean = false): void {
    newAddresses.forEach(data => {
      const newAddress: any = {
        name: this.createAddressString(data),
        value: {
          ...data,
          idCustomerAddress: type === 'customer' ? data.id : null,
          idCompanyUnitAddress: type === 'business' ? data.id : null,
        },
        addressFromSystem,
      };

      const addressFound: any = target.find(address => address.name === newAddress.name);
      if (!addressFound) {
        target.push(newAddress);
      }
    });
  }

  static appendAddressesForCpq(target: Array<any>, newAddresses: Array<any>, billOrPayer: boolean): void {
    newAddresses.forEach(data => {
      const newAddress: any = {
        name: billOrPayer ? this.createAddressStringForCpqSelectedBillOrPayer(data) : this.createAddressStringForCpq(data),
        value: {
          ...data,
        },
      };

      const addressFound: any = target.find(address => address.name === newAddress.name);
      if (!addressFound) {
        target.push(newAddress);
      }
    });
  }

  static isAddressValid(address: IAddress | null | undefined): boolean {
    if (!address) {
      return false;
    }

    const storesWithRequiredStateField: string[] = ['US', 'CA'];
    const isValid: boolean = !!(address.address1 && address.city && address.zipCode);

    return storesWithRequiredStateField.includes(AppUtils.getCurrentStore().storeId) ? isValid && !!address.state : isValid;
  }

  static unifyAddresses(
    address: any[],
    userData: ICustomerCheckoutData,
  ): IAddress[] {
    return address.map(address => {
      return this.unifyAddress(address, userData);
    });
  }

  /**
   * Map not unified address to IAddress type.
   *
   * @static
   *
   * @param {any} address
   * @param {ICustomerCheckoutData} userData
   * @return {IAddress}
   */
  static unifyAddress(
    address: any,
    userData: ICustomerCheckoutData,
  ): IAddress {
    return {
      id: address?.id ?? '',
      sapId: address?.sapId,
      salutation: 'Ms',
      firstName: address?.name ?? address?.company,
      lastName: '',
      address1: address?.address1,
      address2: '',
      zipCode: address?.zipCode,
      city: address?.city,
      state: address?.state,
      iso2Code: address?.iso2Code,
      company: address?.name ?? address?.company,
      phone: userData?.customerData?.phone,
      country: address?.country,
      isDefaultBilling: false,
      isDefaultShipping: false,
      isCustom: address?.isCustom ?? false,
    } as IAddress;
  }

  static updateEmptyAddressFields(address: IAddress): IAddress {
    return {
      id: address?.id ?? '',
      sapId: address?.sapId,
      salutation: address.salutation || 'Ms',
      firstName: address?.firstName || 'empty',
      lastName: address?.lastName || 'empty',
      address1: address?.address1 || 'empty',
      address2: address?.address2 || 'empty',
      address3: '',
      zipCode: address?.zipCode || 'empty',
      city: address?.city || 'empty',
      country: address?.country || 'empty',
      iso2Code: address?.iso2Code || 'empty',
      phone: address?.phone || 'empty',
      company: address?.company || 'empty',
      state: address?.state || 'empty',
      idCustomerAddress: address?.idCustomerAddress,
      idCompanyUnitAddress: address?.idCompanyUnitAddress,
      isDefaultBilling: false,
      isDefaultShipping: false,
      isCustom: address?.isCustom ?? false,
    };
  }

  static mapAddressesToAddressSelectBox(
    addresses: IAddress[],
    alternativeSapId?: string,
    invalidAddressTitle?: string,
  ): IAddressSelectBox[] {
    const selectBoxAddresses: IAddressSelectBox[] = [];
    addresses.forEach(address => {
      const selectBoxAddress: IAddressSelectBox = this.mapAddressToAddressSelectBox(address, alternativeSapId, invalidAddressTitle);
      selectBoxAddresses.push({
        name: selectBoxAddress.name,
        value: selectBoxAddress.value,
      });
    });
    return selectBoxAddresses;
  }

  static mapAddressToAddressSelectBox(
    address: IAddress,
    alternativeSapId?: string,
    invalidAddressTitle?: string,
  ): IAddressSelectBox {
    let addressTitle: string;
    if (address.isCustom) {
      addressTitle = `${this.getFormattedAddress(address)}`;
    } else {
      addressTitle = `${address?.sapId ? address.sapId + ' - ' : ''}${this.getFormattedAddress(address, invalidAddressTitle)}`;
    }
    const addressBody: IAddress = {...address, sapId: address.sapId ?? alternativeSapId};

    return {
      name: addressTitle,
      value: addressBody,
    };
  }

  static getFormattedAddress(
    address: IAddress,
    invalidAddressTitle?: string,
  ): string {
    if (!this.isAddressValid(address)) {
      return invalidAddressTitle ?? '';
    }
    return `${this.formatAddressToString(address)}`;
  }

  static createShipToAddressFromBusinessUnitAddress(
    businessUnit: ICompanyBusinessUnits,
    businessUnitAddress: ICompanyBusinessUnitAddresses,
    userData: ICustomerCheckoutData,
    sapIdFromCart: string,
  ): IAddress {
    return {
      company: businessUnit?.data?.[0]?.attributes?.name,
      isDefaultBilling: false,
      isDefaultShipping: false,
      phone: userData?.customerData?.phone ?? '',
      salutation: userData?.customerData?.salutation ?? '',
      firstName: userData?.customerData?.firstName ?? '',
      lastName: userData?.customerData?.lastName ?? '',
      address1: businessUnitAddress?.attributes?.address1 ?? '',
      address2: businessUnitAddress?.attributes?.address2 ?? '',
      address3: businessUnitAddress?.attributes?.address3,
      city: businessUnitAddress?.attributes?.city ?? '',
      country: businessUnitAddress?.attributes?.country ?? '',
      iso2Code: businessUnitAddress?.attributes?.iso2Code ?? '',
      state: businessUnitAddress?.attributes?.state,
      zipCode: businessUnitAddress?.attributes?.zipCode ?? '',
      sapId: sapIdFromCart,
    };
  }

  /**
   * Checks if the given address field is empty (falsy value or 'empty').
   *
   * @static
   *
   * @param {string | null | undefined} address
   * @returns {boolean}
   */
  static isAddressEmpty(address: string | null | undefined): boolean {
    return !address || address === 'empty';
  }

  /**
   * Returns the display name of a country. BE responses may contain the country either as a plain string
   * or as an ICountry object, even though IAddress types it as a string.
   *
   * @static
   *
   * @param {string | ICountry | null | undefined} country
   * @returns {string}
   */
  static getCountryName(country: string | ICountry | null | undefined): string {
    if (!country) {
      return '';
    }

    return typeof country === 'string' ? country : country.name ?? '';
  }

  /**
   * Checks if the given address is empty.
   *
   * @static
   *
   * @param {string} address
   * @returns {boolean}
   */
  static isAddressObjectEmpty(address: IAddress | null | undefined): boolean {
    if (!address) {
      return true;
    }

    return Object.values(address).every(value => !value);
  }

  /**
   * Mapping table method which returns the address field name of BE response for given address type.
   *
   * @static
   *
   * @param {string} address
   * @param isPurchaseActivityDataStructure
   * @returns {boolean}
   */
  static getAddressFieldName(address: string, isPurchaseActivityDataStructure?: boolean): string {
    switch (address) {
      case EThankYouPageFieldNames.SOLD_TO_ADDRESS:
        return 'soldToAddress';
      case EThankYouPageFieldNames.SHIP_TO_ADDRESS:
        return isPurchaseActivityDataStructure ? 'shipToAddress' : 'shippingAddress';
      case EThankYouPageFieldNames.BILL_TO_ADDRESS:
        return isPurchaseActivityDataStructure ? 'billToAddress' : 'billingAddress';
      case EThankYouPageFieldNames.PAYER_ADDRESS:
        return 'payerAddress';
      default:
        return '';
    }
  }

  /**
   * Format given phone number into numeric string.
   *
   * The '+' symbol is replaced with '00'.
   * All non-numeric characters are removed from the phone number.
   *
   * @static
   *
   * @param {string | null | undefined} phoneNumber
   * @returns {string}
   */
  static formatPhone(phoneNumber: string | null | undefined): string {
    if (!phoneNumber) {
      return '';
    }

    return phoneNumber.replace('+', '00').replace(/[^0-9]/g, '');
  }

  /**
   * Get city and state from address object.
   * Example: Mumbai, MH
   * @param {IShsEquipmentAttributesEquipmentAddress} address
   * @returns {string}
   */
  static getCityAndState(address: IShsEquipmentAttributesEquipmentAddress): string {
    return !address.state || address.city?.includes(address.state)
      ? address.city
      : `${address.city}, ${address.state}`;
  }

  /**
   * Get the country code for the given country name for external quote checkout.
   * Get iso2Code for country. If country does not exist, return default iso2Code (EN).
   * @param {string} country
   * @returns {Iiso2Codes}
   */
  static getCountryForExternalQuoteCheckout(country: string): Iiso2Codes {
    const iso2Codes: Iiso2Codes[] = this.getCountriesForDeliveryAddress();
    return (iso2Codes.find(i => i.value === country) || iso2Codes.find(i => i.isDefault))!;
  }
}
